import { useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { CreditCard, Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'

type Disposition = 'would_backfill' | 'skip_super_admin' | 'skip_no_admin'

interface Candidate {
  orgId: string
  orgName: string
  adminEmail: string | null
  currentTier: string | null
  currentStatus: string | null
  trialEndsAt: string | null
  disposition: Disposition
  reason?: string
}

interface PreviewData {
  counts: {
    total: number
    wouldBackfill: number
    skipSuperAdmin: number
    skipNoAdmin: number
  }
  candidates: Candidate[]
}

interface RunResult {
  orgId: string
  orgName: string
  status: 'created' | 'failed'
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  error?: string
}

interface RunData {
  summary: { processed: number; created: number; failed: number; skipped: number }
  results: RunResult[]
}

const DISPOSITION_LABEL: Record<Disposition, { label: string; tone: 'default' | 'secondary' | 'outline' }> = {
  would_backfill: { label: 'Will backfill', tone: 'default' },
  skip_super_admin: { label: 'Skip — super admin', tone: 'secondary' },
  skip_no_admin: { label: 'Skip — no admin', tone: 'outline' },
}

export default function StripeBackfillCard() {
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [runResult, setRunResult] = useState<RunData | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [running, setRunning] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const { toast } = useToast()

  const loadPreview = async () => {
    setLoadingPreview(true)
    setRunResult(null)
    try {
      const res = await api.get('/api/platform/billing/backfill-stripe/preview')
      setPreview(res.data.data)
    } catch (err: any) {
      toast({
        title: 'Preview failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setLoadingPreview(false)
    }
  }

  const runBackfill = async () => {
    if (confirmText !== 'BACKFILL') return
    setRunning(true)
    try {
      const res = await api.post('/api/platform/billing/backfill-stripe/run', { confirm: 'BACKFILL' })
      setRunResult(res.data.data)
      setConfirmText('')
      await loadPreview()
      toast({
        title: 'Backfill complete',
        description: `${res.data.data.summary.created} created · ${res.data.data.summary.failed} failed · ${res.data.data.summary.skipped} skipped`,
      })
    } catch (err: any) {
      toast({
        title: 'Backfill failed',
        description: err.response?.data?.message ?? err.message,
        variant: 'destructive',
      })
    } finally {
      setRunning(false)
    }
  }

  const canRun =
    preview && preview.counts.wouldBackfill > 0 && confirmText === 'BACKFILL' && !running

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-brand-royal" />
          <CardTitle className="text-base">Stripe customer backfill</CardTitle>
        </div>
        <CardDescription>
          Heals zombie orgs — orgs marked trialing in our DB but missing from Stripe. Until this runs,
          their Billing page throws "No Stripe customer" on every action.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!preview && (
          <Button onClick={loadPreview} disabled={loadingPreview} variant="outline">
            {loadingPreview ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scanning…
              </>
            ) : (
              'Scan for zombie orgs'
            )}
          </Button>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Found</p>
                <p className="text-xl font-semibold">{preview.counts.total}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Will backfill</p>
                <p className="text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                  {preview.counts.wouldBackfill}
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Skip — super admin</p>
                <p className="text-xl font-semibold text-muted-foreground">
                  {preview.counts.skipSuperAdmin}
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-xs text-muted-foreground">Skip — no admin</p>
                <p className="text-xl font-semibold text-muted-foreground">
                  {preview.counts.skipNoAdmin}
                </p>
              </div>
            </div>

            {preview.candidates.length > 0 && (
              <div className="rounded-lg border divide-y max-h-80 overflow-y-auto">
                {preview.candidates.map(c => (
                  <div key={c.orgId} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{c.orgName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {c.adminEmail ?? 'no admin'} · tier: {c.currentTier ?? '—'} · status:{' '}
                        {c.currentStatus ?? '—'}
                      </p>
                      {c.reason && (
                        <p className="text-xs text-muted-foreground mt-0.5 italic">{c.reason}</p>
                      )}
                    </div>
                    <Badge variant={DISPOSITION_LABEL[c.disposition].tone} className="shrink-0">
                      {DISPOSITION_LABEL[c.disposition].label}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {preview.counts.wouldBackfill > 0 && !runResult && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-3">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <p>
                    This creates <span className="font-semibold">{preview.counts.wouldBackfill}</span>{' '}
                    Stripe customers and trial subscriptions. Stripe resources can be deleted but it's
                    messy — only run this when you mean it.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Type BACKFILL to confirm"
                    disabled={running}
                    className="flex-1"
                  />
                  <Button
                    onClick={runBackfill}
                    disabled={!canRun}
                    variant="destructive"
                  >
                    {running ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Running…
                      </>
                    ) : (
                      'Run backfill'
                    )}
                  </Button>
                </div>
              </div>
            )}

            <Button onClick={loadPreview} disabled={loadingPreview || running} variant="ghost" size="sm">
              {loadingPreview ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Refreshing…
                </>
              ) : (
                'Rescan'
              )}
            </Button>
          </>
        )}

        {runResult && (
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <p className="text-sm font-medium">
              Processed {runResult.summary.processed} · Created {runResult.summary.created} · Failed{' '}
              {runResult.summary.failed}
            </p>
            {runResult.results.length > 0 && (
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {runResult.results.map(r => (
                  <div key={r.orgId} className="flex items-center gap-2 text-xs">
                    {r.status === 'created' ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    )}
                    <span className="font-medium truncate">{r.orgName}</span>
                    {r.stripeCustomerId && (
                      <span className="text-muted-foreground font-mono">{r.stripeCustomerId}</span>
                    )}
                    {r.error && <span className="text-red-500">{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
